import React, { useReducer, useEffect } from 'react'
import PropTypes from 'prop-types'
import { pipe, last, path, pick, map, equals } from 'ramda'
import { useCMSEntries } from 'mage-cms'
import { useEducationAnalytics } from 'education-tools'
import HelpContext from './HelpContext'
import useLocalStorage from './hooks/useLocalStorage'
import reducer from './HelpContextReducer'
import actions from './HelpContextActions'
import { getInitialState, persistentStateKeys } from './HelpContextState'
import {
  UHQM_HIDDEN_POPUPS_LOCAL_STORAGE_KEY,
  UHQM_SIDEBAR_ANIMATION_DURATION,
  UHQM_SESSION_STORAGE_KEY
} from '../../constants'

HelpContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  location: PropTypes.object,
  history: PropTypes.object,
  sidebarAnimationDuration: PropTypes.number
}

export const getActivePage = pipe(path(['navigationStack']), last)

const handleBeforeUnload = state => () => {
  const persistentState = pick(persistentStateKeys, state)
  window.sessionStorage.setItem(UHQM_SESSION_STORAGE_KEY, JSON.stringify(persistentState))
}

export default function HelpContextProvider({
  children,
  location = window.location,
  history = window.history,
  sidebarAnimationDuration = UHQM_SIDEBAR_ANIMATION_DURATION
}) {
  const [state, dispatch] = useReducer(reducer, getInitialState(sidebarAnimationDuration, true))

  const hiddenPopupEntries = useCMSEntries({
    contentType: 'UHQMPopup',
    filters: {
      'slug[in]': state.popup.hiddenPopups.map(popup => popup.slug)
    }
  })

  const addTopic = slug => dispatch({ type: actions.ADD_TOPIC, slug })
  const removeTopic = slug => dispatch({ type: actions.REMOVE_TOPIC, slug })
  const addPopup = popupSlug => dispatch({ type: actions.ADD_POPUP, popupSlug })
  const removePopup = popupSlug => dispatch({ type: actions.REMOVE_POPUP, popupSlug })
  const setIsOpen = isOpen => dispatch({ type: actions.SET_SIDEBAR_OPEN, isOpen: !!isOpen })
  const setIsOpening = isOpening =>
    dispatch({ type: actions.SET_SIDEBAR_OPENING, isOpening: !!isOpening })
  const close = () => dispatch({ type: actions.SET_SIDEBAR_OPEN, isOpen: false })
  const setSearchQuery = query => dispatch({ type: actions.SET_SEARCH_QUERY, query })
  const setIsSearching = isSearching => dispatch({ type: actions.SET_IS_SEARCHING, isSearching })
  const hidePopup = popupSlug => dispatch({ type: actions.HIDE_POPUP, popupSlug })
  const setHiddenPopups = popups => dispatch({ type: actions.SET_HIDDEN_POPUPS, popups })
  const reset = () => dispatch({ type: actions.RESET })

  const navigation = {
    push: (path, parameters) => dispatch({ type: actions.NAVIGATE_PUSH, path, parameters }),
    pop: () => dispatch({ type: actions.NAVIGATE_POP }),
    replace: (path, parameters) => dispatch({ type: actions.NAVIGATE_REPLACE, path, parameters }),
    reset: () => dispatch({ type: actions.NAVIGATE_RESET }),
    activePage: getActivePage(state),
    stacksize: state.navigationStack.length,
    openHelpPage: openHelpPage
  }

  // Removes topics with expired dismissal from local storage
  useEffect(() => {
    const nonExpiredHiddenTopics = state.popup.hiddenPopups.filter(
      popup => !canShowPopup(state.popup.hiddenPopups, hiddenPopupEntries, popup.slug)
    )

    if (!equals(state.popup.hiddenPopups, nonExpiredHiddenTopics)) {
      setHiddenPopups(nonExpiredHiddenTopics)
    }
  }, [state.popup.hiddenPopups, hiddenPopupEntries])

  useLocalStorage(
    UHQM_HIDDEN_POPUPS_LOCAL_STORAGE_KEY,
    state.popup.hiddenPopups,
    pipe(map(convertHiddenTopicsToNewFormat), setHiddenPopups)
  )

  useEffect(() => {
    const handler = handleBeforeUnload(state)
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [state])

  useEffect(() => {
    if (state.sidebar.isOpening) {
      const handler = setTimeout(() => {
        setIsOpening(false)
      }, sidebarAnimationDuration)

      return () => {
        setIsOpening(false)
        clearTimeout(handler)
      }
    }
  }, [state.sidebar.isOpening])

  const trackDeepLink = useEducationAnalytics({
    category: 'uhqm/deep-link',
    action: 'open'
  })

  function openHelpPage(helpPage) {
    const [page, slug] = helpPage.split(':')

    switch (page) {
      case 'home':
        navigation.reset()
        setIsOpen(true)
        break
      case 'article':
        navigation.push('article', { articleSlug: slug })
        break
      case 'topic':
        navigation.push('topic', { topicSlug: slug })
        break
      case 'tour':
        navigation.push('tour', { tourSlug: slug })
        break
    }
  }

  useEffect(() => {
    const search = new URLSearchParams(location.search)

    const helpPage = search.get('help-page')

    if (helpPage) {
      trackDeepLink({ label: helpPage })

      openHelpPage(helpPage)

      search.delete('help-page')

      const separator = search.toString().length > 0 ? '?' : ''

      history.replaceState({}, document.title, `${window.location.pathname}${separator}${search}`)
    }
  }, [])

  const popupSlug = state.sidebar.isOpen
    ? null
    : state.popup.popupSlugs.find(popupSlug =>
        canShowPopup(state.popup.hiddenPopups, hiddenPopupEntries, popupSlug)
      )

  return (
    <HelpContext.Provider
      value={{
        isOpen: state.sidebar.isOpen,
        topics: state.topics,
        popup: {
          popupSlug
        },
        sidebar: state.sidebar,
        close,
        addTopic,
        removeTopic,
        addPopup,
        removePopup,
        setIsOpen,
        navigation,
        searchQuery: navigation.activePage.searchQuery,
        setSearchQuery,
        isSearching: navigation.activePage.isSearching,
        setIsSearching,
        hidePopup,
        reset
      }}
    >
      {children}
    </HelpContext.Provider>
  )
}

/* Converts local storage hidden popups from old format ['slug'] to new format [{ slug, hiddenAt }] */
function convertHiddenTopicsToNewFormat(hiddenTopic) {
  const isOldFormat = typeof hiddenTopic === 'string'
  return isOldFormat ? { slug: hiddenTopic, hiddenAt: Date.now() } : hiddenTopic
}

function canShowPopup(hiddenPopups, hiddenPopupEntries, popupSlug) {
  if (!hiddenPopupEntries) return false

  const hiddenPopup = hiddenPopups.find(hiddenPopup => hiddenPopup.slug === popupSlug)

  if (!hiddenPopup) return true

  const hiddenPopupEntry = hiddenPopupEntries.find(
    hiddenPopupEntry => hiddenPopupEntry.slug === popupSlug
  )

  if (!hiddenPopupEntry || !hiddenPopupEntry.popupHideDuration) return false

  return isNowAfter(hiddenPopup.hiddenAt, hiddenPopupEntry.popupHideDuration)
}

function isNowAfter(baseDate, durationInDays) {
  return Date.now() > baseDate + durationInDays * 24 * 60 * 60 * 1000
}
